import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  createOcrProvider,
  deleteOcrProvider,
  getOcrProviderModels,
  listOcrProviders,
  testOcrProvider,
  updateOcrProvider,
  type CreateOcrProviderRequest,
  type OcrProviderKind,
  type OcrProviderModelsResponse,
  type OcrProviderResponse,
  type ProviderTestResponse,
  type UpdateOcrProviderRequest,
} from '../api';

export function useOcrProviders(): UseQueryResult<OcrProviderResponse[]> {
  return useQuery({
    queryKey: AdminQueryKeys.ocrProviders(),
    queryFn: ({ signal }) => listOcrProviders(signal),
    staleTime: 30_000,
  });
}

/** Suggested models for a provider kind. Free text is still accepted in the form. */
export function useOcrProviderModels(kind: OcrProviderKind): UseQueryResult<OcrProviderModelsResponse> {
  return useQuery({
    queryKey: AdminQueryKeys.ocrProviderModels(kind),
    queryFn: ({ signal }) => getOcrProviderModels(kind, signal),
    staleTime: 5 * 60_000,
  });
}

export function useCreateOcrProvider() {
  const qc = useQueryClient();
  return useMutation<OcrProviderResponse, Error, CreateOcrProviderRequest>({
    mutationFn: (req) => createOcrProvider(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.ocrProviders() });
    },
  });
}

export function useUpdateOcrProvider() {
  const qc = useQueryClient();
  return useMutation<OcrProviderResponse, Error, { id: string; request: UpdateOcrProviderRequest }>({
    mutationFn: ({ id, request }) => updateOcrProvider(id, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.ocrProviders() });
    },
  });
}

export function useDeleteOcrProvider() {
  const qc = useQueryClient();
  return useMutation<null, Error, string>({
    mutationFn: (id) => deleteOcrProvider(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.ocrProviders() });
    },
  });
}

export function useTestOcrProvider() {
  return useMutation<ProviderTestResponse, Error, string>({
    mutationFn: (id) => testOcrProvider(id),
  });
}
